import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import Breadcrumbs from "../../../Common/Breadcrumbs";
import ShimmerLoader from "../../../Common/ShimmerLoader";
import TablePaginationFooter from "../../../components/ui/molecules/TablePaginationFooter";
import { useDebounce } from "../../../hooks/useDebounce";
import { PermissionWrapper } from "../../../components/PermissionWrapper/PermissionWrapper";
import { SuccessModal } from "../../../Common/SuccessModal";

import { useAdvertisers } from "../hooks";
import type { Advertiser, CreateAdvertiserPayload } from "../../../api/ads";

import { AdvertiserTable } from "./components/AdvertiserTable";
import { CreateAdvertiserModal } from "./components/CreateAdvertiserModal";

import "./Advertisers.css";

const AdvertisersPage: React.FC = () => {
  const navigate = useNavigate();

  const { functions, values, loading } = useAdvertisers();

  const [searchInput, setSearchInput] = useState<string>(values.params.search || "");
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    functions.setParams({ search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const pageSize = values.params.pageSize ?? 10;
  const currentPage = values.params.pageNum ?? 1;

  const advertisers = values.advertisers;

  const formattedTotalCount = useMemo(() => values.totalCount ?? 0, [values.totalCount]);

  // Create advertiser modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState("Success");
  const [successMessage, setSuccessMessage] = useState("");

  const handleCreateSubmit = useCallback(
    async (payload: CreateAdvertiserPayload) => {
      setCreateError(null);
      try {
        const message = await functions.createAdvertiser(payload);
        setShowCreateModal(false);
        setSuccessTitle("Advertiser Created");
        setSuccessMessage(message || `Advertiser "${payload.name}" created successfully.`);
        setShowSuccessModal(true);
      } catch (err: any) {
        setCreateError(err?.message || "Failed to create advertiser. Please try again.");
      }
    },
    [functions]
  );

  const handleStatusChange = useCallback(
    async (action: (id: string) => Promise<string>, id: string) => {
      try {
        const message = await action(id);
        setSuccessTitle("Advertiser Updated");
        setSuccessMessage(message || "Advertiser status updated successfully.");
        setShowSuccessModal(true);
      } catch {
        // error is surfaced via values.error alert
      }
    },
    []
  );

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Ads", onClick: () => navigate("/ads/overview") },
              { label: "Advertisers", active: true },
            ]}
          />
        </div>

        <div className="mb-4" style={{ padding: "0 15px" }}>
          <h1 className="ads-page-title">Advertisers</h1>
          <p className="ads-page-subtitle">Total {formattedTotalCount} Advertisers</p>
        </div>

        <Row>
          <Col>
            <Card className="ads-page-card">
              <Card.Body className="ads-page-card__body">
                <div className="ads-toolbar">
                  <div className="ads-searchbar ads-searchbar--toolbar">
                    <div className="ads-searchbar__icon">
                      <i className="ph-magnifying-glass" />
                    </div>
                    <input
                      type="text"
                      className="ads-searchbar__input"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search advertisers..."
                      aria-label="Search advertisers"
                    />
                    {searchInput && (
                      <button type="button" className="ads-searchbar__clear" onClick={() => setSearchInput("")} aria-label="Clear search">
                        <i className="ph-x" />
                      </button>
                    )}
                  </div>

                  <div className="ads-toolbar__actions">
                    <PermissionWrapper anyOfPermissions={["ads.admin", "ads.manage"]} hideWhenDisabled>
                      <Button
                        variant="primary"
                        className="ads-create-advertiser-btn"
                        onClick={() => {
                          setCreateError(null);
                          setShowCreateModal(true);
                        }}
                      >
                        + Create Advertiser
                      </Button>
                    </PermissionWrapper>
                  </div>
                </div>

                {loading.listLoading ? (
                  <ShimmerLoader type="table" rows={5} />
                ) : advertisers.length > 0 ? (
                  <AdvertiserTable
                    advertisers={advertisers}
                    onApprove={(id) => handleStatusChange(functions.approveAdvertiser, id)}
                    onReject={(id) => handleStatusChange(functions.rejectAdvertiser, id)}
                    onBlock={(id) => handleStatusChange(functions.blockAdvertiser, id)}
                    onViewCampaigns={(adv: Advertiser) =>
                      navigate(`/ads/campaigns?advertiser_id=${encodeURIComponent(adv.id)}`)
                    }
                    onViewBilling={(adv: Advertiser) =>
                      navigate(`/ads/billing?advertiser_id=${encodeURIComponent(adv.id)}`)
                    }
                  />
                ) : (
                  <div className="ads-empty">
                    <p className="ads-empty__text">No advertisers found.</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {!loading.listLoading && formattedTotalCount > 0 && (
              <TablePaginationFooter
                page={currentPage}
                totalPages={Math.max(1, values.totalPages)}
                pageSize={pageSize}
                totalCount={formattedTotalCount}
                onPageChange={functions.setPageNum}
                onPageSizeChange={functions.setPageSize}
              />
            )}
          </Col>
        </Row>

        {values.error && (
          <div style={{ padding: "0 15px" }}>
            <Alert variant="danger" dismissible onClose={() => functions.fetchAdvertisers()}>
              {values.error}
            </Alert>
          </div>
        )}
      </Container>

      <CreateAdvertiserModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
        submitting={loading.createLoading}
        error={createError}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        title={successTitle}
        message={successMessage}
      />
    </div>
  );
};

export default AdvertisersPage;
